/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { deflate, inflate } from 'zlib';
import { promisify } from 'util';

const deflateAsync = promisify(deflate);
const inflateAsync = promisify(inflate);

interface CacheEntry {
  etag: string;
  body: Buffer;
  byteSize: number;
}

interface EtagCacheConfig {
  maxEntries?: number;
  maxBytes?: number;
}

const DEFAULT_MAX_ENTRIES = 5000;
const DEFAULT_MAX_BYTES = 200 * 1024 * 1024; // 200 MB

const store = new Map<string, CacheEntry>();
let totalBytes = 0;

export const getStore = (): Map<string, CacheEntry> => store;
export const getTotalBytes = (): number => totalBytes;

export const ETAG_CACHE_HIT = Symbol('etagCacheHit');

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    [ETAG_CACHE_HIT]?: boolean;
  }
}

function buildCacheKey(config: InternalAxiosRequestConfig): string {
  const base = config.baseURL ?? '';
  const url = config.url ?? '';
  const fullUrl = url.startsWith('http') ? url : `${base}${url}`;

  const parts: string[] = [fullUrl];

  const params = config.params as Record<string, unknown> | undefined;
  if (params && Object.keys(params).length > 0) {
    const sorted = Object.entries(params)
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, val]) => `${key}=${Array.isArray(val) ? val.map(String).join(',') : String(val)}`)
      .join('&');
    parts.push(`?${sorted}`);
  }

  return parts.join('');
}

function buildVaryKey(config: InternalAxiosRequestConfig, varyHeader: string): string {
  const varyFields = varyHeader
    .split(',')
    .map((field) => field.trim().toLowerCase())
    .filter(Boolean)
    .sort();

  const headerValues = varyFields
    .map((field) => {
      const val = config.headers?.get?.(field) ?? config.headers?.[field] ?? '';
      return `${field}=${String(val)}`;
    })
    .join('|');

  return headerValues;
}

function isGetRequest(config: InternalAxiosRequestConfig): boolean {
  return (config.method ?? 'get').toLowerCase() === 'get';
}

function hasNoStore(headers: Record<string, unknown>): boolean {
  const cacheControl = headers['cache-control'];
  if (typeof cacheControl !== 'string') return false;
  return cacheControl.includes('no-store');
}

function promoteLru(key: string): void {
  const entry = store.get(key);
  if (!entry) return;
  store.delete(key);
  store.set(key, entry);
}

function evict(maxEntries: number, maxBytes: number, incomingBytes: number): void {
  while (store.size > 0 && (store.size >= maxEntries || totalBytes + incomingBytes > maxBytes)) {
    const firstKey = store.keys().next().value as string;
    const evicted = store.get(firstKey);
    if (evicted) {
      totalBytes -= evicted.byteSize;
    }
    store.delete(firstKey);
  }
}

const attachedInstances = { current: new WeakSet<AxiosInstance>() };

export const attachEtagInterceptors = (instance: AxiosInstance, config: EtagCacheConfig = {}): void => {
  if (attachedInstances.current.has(instance)) return;
  attachedInstances.current.add(instance);

  const maxEntries = config.maxEntries ?? DEFAULT_MAX_ENTRIES;
  const maxBytes = config.maxBytes ?? DEFAULT_MAX_BYTES;

  instance.interceptors.request.use((reqConfig) => {
    if (!isGetRequest(reqConfig)) return reqConfig;
    const key = buildCacheKey(reqConfig);
    const prefix = `${key}|vary=`;
    for (const [storeKey] of store) {
      if (storeKey === key || storeKey.startsWith(prefix)) {
        const cached = store.get(storeKey);
        if (cached) {
          reqConfig.headers.set('If-None-Match', cached.etag);
          promoteLru(storeKey);
        }
        break;
      }
    }
    return reqConfig;
  });

  instance.interceptors.response.use(
    async (response: AxiosResponse) => {
      if (!isGetRequest(response.config)) return response;

      const responseHeaders = response.headers as Record<string, unknown>;
      if (hasNoStore(responseHeaders)) return response;

      const { etag } = responseHeaders as { etag?: string };
      if (typeof etag !== 'string' || etag.length === 0) return response;

      let key = buildCacheKey(response.config);
      const vary = responseHeaders.vary as string | undefined;
      if (typeof vary === 'string' && vary.length > 0) {
        key = `${key}|vary=${buildVaryKey(response.config, vary)}`;
      }

      const compressed = await deflateAsync(Buffer.from(JSON.stringify(response.data)));
      const byteSize = compressed.length;
      evict(maxEntries, maxBytes, byteSize);
      store.set(key, { etag, body: compressed, byteSize });
      totalBytes += byteSize;

      return response;
    },
    async (error: unknown) => {
      if (!(error instanceof AxiosError) || error.response?.status !== 304 || !error.config) {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      const key = buildCacheKey(error.config);
      let cached: CacheEntry | undefined;
      const prefix = `${key}|vary=`;
      for (const [storeKey, entry] of store) {
        if (storeKey === key || storeKey.startsWith(prefix)) {
          cached = entry;
          promoteLru(storeKey);
          break;
        }
      }

      if (!cached) {
        return Promise.reject(error);
      }

      const decompressed = await inflateAsync(cached.body);
      const data: unknown = JSON.parse(decompressed.toString());
      const syntheticResponse: AxiosResponse = {
        data,
        status: 200,
        statusText: 'Not Modified',
        headers: error.response.headers,
        config: { ...error.config, [ETAG_CACHE_HIT]: true },
        request: error.response.request,
      };

      return syntheticResponse;
    },
  );
};

export const resetForTesting = (): void => {
  store.clear();
  totalBytes = 0;
  attachedInstances.current = new WeakSet();
};
